import matter from 'gray-matter';
import * as crypto from 'crypto';

export interface ParsedMarkdown {
  content: string;
  data: Record<string, any>;
  excerpt?: string;
  toc?: TableOfContentsItem[];
  wordCount?: number;
  readingTime?: number;
}

export interface TableOfContentsItem {
  id: string;
  text: string;
  level: number;
  children: TableOfContentsItem[];
}

export interface MarkdownSection {
  id: string;
  title: string;
  content: string;
  level: number;
}

export interface MarkdownParserOptions {
  excerpt?: boolean;
  excerptSeparator?: string;
  generateTOC?: boolean;
  calculateStats?: boolean;
  extractSections?: boolean;
}

export class MarkdownParser {
  private defaultOptions: MarkdownParserOptions = {
    excerpt: true,
    excerptSeparator: '<!-- more -->',
    generateTOC: true,
    calculateStats: true,
    extractSections: false,
  };

  /**
   * Parse markdown content with frontmatter
   */
  parse(content: string, options?: MarkdownParserOptions): ParsedMarkdown {
    const opts = { ...this.defaultOptions, ...options };
    
    // Parse frontmatter
    const { content: markdownContent, data, excerpt } = matter(content, {
      excerpt: opts.excerpt,
      excerpt_separator: opts.excerptSeparator,
    });

    const result: ParsedMarkdown = {
      content: markdownContent,
      data,
      excerpt,
    };

    // Generate table of contents
    if (opts.generateTOC) {
      result.toc = this.generateTableOfContents(markdownContent);
    }

    // Calculate stats
    if (opts.calculateStats) {
      const stats = this.calculateStats(markdownContent);
      result.wordCount = stats.wordCount;
      result.readingTime = stats.readingTime;
    }

    return result;
  }

  /**
   * Extract all headings with hierarchy
   */
  extractHeadings(content: string): Array<{ text: string; level: number; id: string }> {
    const headingPattern = /^(#{1,6})\s+(.+)$/gm;
    const headings: Array<{ text: string; level: number; id: string }> = [];
    let match;

    while ((match = headingPattern.exec(content)) !== null) {
      const level = match[1].length;
      const text = match[2].trim();
      const id = this.createSlug(text);
      
      headings.push({ text, level, id });
    }

    return headings;
  }

  /**
   * Generate table of contents with nested structure
   */
  generateTableOfContents(content: string): TableOfContentsItem[] {
    const headings = this.extractHeadings(content);
    const toc: TableOfContentsItem[] = [];
    const stack: TableOfContentsItem[] = [];

    headings.forEach(heading => {
      const item: TableOfContentsItem = {
        id: heading.id,
        text: heading.text,
        level: heading.level,
        children: [],
      };

      // Find parent level
      while (stack.length > 0 && stack[stack.length - 1].level >= heading.level) {
        stack.pop();
      }

      // Add to parent or root
      if (stack.length === 0) {
        toc.push(item);
      } else {
        stack[stack.length - 1].children.push(item);
      }

      stack.push(item);
    });

    return toc;
  }

  /**
   * Extract sections by headings
   */
  extractSections(content: string): MarkdownSection[] {
    const lines = content.split('\n');
    const sections: MarkdownSection[] = [];
    let currentSection: MarkdownSection | null = null;
    let sectionContent: string[] = [];

    lines.forEach(line => {
      const headingMatch = line.match(/^(#{1,6})\s+(.+)$/);
      
      if (headingMatch) {
        // Save previous section
        if (currentSection) {
          currentSection.content = sectionContent.join('\n').trim();
          sections.push(currentSection);
        }

        // Start new section
        const level = headingMatch[1].length;
        const title = headingMatch[2].trim();
        currentSection = {
          id: this.createSlug(title),
          title,
          content: '',
          level,
        };
        sectionContent = [];
      } else if (currentSection) {
        sectionContent.push(line);
      }
    });

    // Save last section
    if (currentSection) {
      (currentSection as MarkdownSection).content = sectionContent.join('\n').trim();
      sections.push(currentSection);
    }

    return sections;
  }

  /**
   * Extract links from markdown
   */
  extractLinks(content: string): Array<{ text: string; url: string; title?: string }> {
    const links: Array<{ text: string; url: string; title?: string }> = [];
    
    // Inline links: [text](url "title")
    const inlineLinkPattern = /\[([^\]]+)\]\(([^)]+?)(?:\s+"([^"]+)")?\)/g;
    let match;
    
    while ((match = inlineLinkPattern.exec(content)) !== null) {
      links.push({
        text: match[1],
        url: match[2],
        title: match[3],
      });
    }

    // Reference links: [text][ref]
    const refLinkPattern = /\[([^\]]+)\]\[([^\]]+)\]/g;
    const refDefinitions = this.extractReferenceDefinitions(content);
    
    while ((match = refLinkPattern.exec(content)) !== null) {
      const ref = match[2];
      if (refDefinitions[ref]) {
        links.push({
          text: match[1],
          url: refDefinitions[ref].url,
          title: refDefinitions[ref].title,
        });
      }
    }

    return links;
  }

  /**
   * Extract reference link definitions
   */
  private extractReferenceDefinitions(content: string): Record<string, { url: string; title?: string }> {
    const definitions: Record<string, { url: string; title?: string }> = {};
    const pattern = /^\[([^\]]+)\]:\s+(\S+)(?:\s+"([^"]+)")?$/gm;
    let match;

    while ((match = pattern.exec(content)) !== null) {
      definitions[match[1]] = {
        url: match[2],
        title: match[3],
      };
    }

    return definitions;
  }

  /**
   * Extract code blocks
   */
  extractCodeBlocks(content: string): Array<{ lang?: string; code: string }> {
    const blocks: Array<{ lang?: string; code: string }> = [];
    const pattern = /```(\w+)?\n([\s\S]*?)```/g;
    let match;

    while ((match = pattern.exec(content)) !== null) {
      blocks.push({
        lang: match[1],
        code: match[2].trim(),
      });
    }

    return blocks;
  }

  /**
   * Calculate word count and reading time
   */
  calculateStats(content: string): { wordCount: number; readingTime: number } {
    // Remove code blocks
    const withoutCode = content.replace(/```[\s\S]*?```/g, '');
    
    // Remove markdown syntax
    const plainText = withoutCode
      .replace(/^#{1,6}\s+/gm, '') // Headers
      .replace(/[*_~`]/g, '') // Emphasis
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // Links
      .replace(/!\[([^\]]*)\]\([^)]+\)/g, '') // Images
      .replace(/^\s*[-*+]\s+/gm, '') // Lists
      .replace(/^\s*\d+\.\s+/gm, '') // Numbered lists
      .replace(/^\s*>/gm, ''); // Blockquotes

    // Count words
    const words = plainText.match(/\b\w+\b/g) || [];
    const wordCount = words.length;

    // Calculate reading time (assuming 200 words per minute)
    const readingTime = Math.ceil(wordCount / 200);

    return { wordCount, readingTime };
  }

  /**
   * Create URL-friendly slug from text
   */
  private createSlug(text: string): string {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  /**
   * Parse markdown table
   */
  parseTables(content: string): Array<{ headers: string[]; rows: string[][] }> {
    const tables: Array<{ headers: string[]; rows: string[][] }> = [];
    const lines = content.split('\n');
    let i = 0;

    while (i < lines.length) {
      // Look for table header
      if (i + 1 < lines.length && 
          lines[i].includes('|') && 
          lines[i + 1].match(/^\s*\|?\s*:?-+:?\s*\|/)) {
        
        const headers = lines[i]
          .split('|')
          .map(h => h.trim())
          .filter(h => h);
        
        const rows: string[][] = [];
        i += 2; // Skip header and separator
        
        // Parse rows
        while (i < lines.length && lines[i].includes('|')) {
          const row = lines[i]
            .split('|')
            .map(cell => cell.trim())
            .filter(cell => cell);
          
          if (row.length > 0) {
            rows.push(row);
          }
          i++;
        }
        
        tables.push({ headers, rows });
      } else {
        i++;
      }
    }

    return tables;
  }

  /**
   * Convert markdown to plain text
   */
  toPlainText(content: string): string {
    return content
      .replace(/```[\s\S]*?```/g, '') // Remove code blocks
      .replace(/^#{1,6}\s+/gm, '') // Remove headers
      .replace(/[*_~]/g, '') // Remove emphasis
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // Convert links to text
      .replace(/!\[([^\]]*)\]\([^)]+\)/g, '') // Remove images
      .replace(/^\s*[-*+]\s+/gm, '') // Remove list markers
      .replace(/^\s*\d+\.\s+/gm, '') // Remove numbered list markers
      .replace(/^\s*>/gm, '') // Remove blockquotes
      .replace(/\n{3,}/g, '\n\n') // Normalize line breaks
      .trim();
  }

  /**
   * Generate a content hash for caching
   */
  generateHash(content: string): string {
    return crypto.createHash('sha256').update(content).digest('hex');
  }
}